<template>
    <v-form @submit.prevent="addEditOvertime" ref="form">
        <v-card dense class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">
                    {{ overtime.mode }} Overtime
                </span>
                <v-spacer></v-spacer>
                <help-link appId="attendance" :pageId="showTransDatePicker ? 'overtime-page' : 'overtime-daily'" color="black"/>
                <v-btn icon @click.native="closeDialog" :disabled="disabled">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-alert v-if="isApproved" :value="true" color="warning" icon="warning" outline>
                This is already approved. Clicking Save will update back to For Review.
            </v-alert>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support@sweldonow.ph.
                Error details: {{ errorMessage }}
            </v-alert>

            <v-card-text :class="isMobile ? 'form-content-mobile' : 'form-content'">

                <!-- DATE -->
                <template v-if="showTransDatePicker">
                    <v-layout row>
                        <v-flex xs12>
                            <v-menu
                                v-model="menuTransDate"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="transDate"
                                        label="Date"
                                        readonly
                                        v-on="on"
                                        :rules="[rules.required]"
                                        :disabled="disabled"
                                    />
                                </template>

                                <v-date-picker
                                    v-model="transDate"
                                    :allowed-dates="allowedDates"
                                    @input="menuTransDate = false"
                                />
                            </v-menu>
                        </v-flex>
                    </v-layout>
                </template>

                <template v-else>
                    <v-layout row>
                        <v-flex xs3>
                            <b>Date:</b>
                        </v-flex>

                        <v-flex xs9>
                            <b>{{ overtime.transDate | moment('YYYY-MM-DD') }}</b>
                        </v-flex>
                    </v-layout>
                </template>

                <!-- TIME -->
                <v-layout row wrap class="mt-10">

                    <v-flex xs12 sm6 :class="isMobile ? '' : 'pr-10'">

                        <v-menu
                            ref="menuClockStart"
                            v-model="menuClockStart"
                            :close-on-content-click="false"
                            :return-value.sync="timeClockStart"
                            transition="scale-transition"
                            offset-y
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="timeClockStart"
                                    label="Start Time"
                                    readonly
                                    v-on="on"
                                    :rules="[rules.required]"
                                />
                            </template>

                            <v-time-picker
                                v-if="menuClockStart"
                                v-model="timeClockStart"
                                @click:minute="$refs.menuClockStart.save(timeClockStart)"
                                full-width
                                scrollable
                                format="ampm"
                            />
                        </v-menu>

                    </v-flex>

                    <v-flex xs12 sm6 :class="isMobile ? '' : 'pl-10'">

                        <v-menu
                            ref="menuClockEnd"
                            v-model="menuClockEnd"
                            :close-on-content-click="false"
                            :return-value.sync="timeClockEnd"
                            transition="scale-transition"
                            offset-y
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="timeClockEnd"
                                    label="End Time"
                                    readonly
                                    v-on="on"
                                    :rules="[rules.required]"
                                />
                            </template>

                            <v-time-picker
                                v-if="menuClockEnd"
                                v-model="timeClockEnd"
                                @click:minute="$refs.menuClockEnd.save(timeClockEnd)"
                                full-width
                                scrollable
                                format="ampm"
                            />
                        </v-menu>

                    </v-flex>

                </v-layout>

                <!-- CHARGING -->
                <v-layout row wrap>

                    <v-flex xs12 sm6 :class="isMobile ? '' : 'pr-10'">

                        <v-select
                            label="Charging Type"
                            :items="offsetOrPay_list"
                            item-text="Description"
                            item-value="offsetOrPay"
                            :rules="[rules.required]"
                            v-model="overtime.offsetOrPay"
                            :disabled="disabled"
                            @change="setRateType"
                        />

                    </v-flex>

                    <v-flex xs12 sm6 :class="isMobile ? '' : 'pl-10'">

                        <v-select
                            label="Rate Type"
                            :items="overtimeRateTypesOptions"
                            item-text="description"
                            item-value="rateType"
                            :rules="[rules.required]"
                            v-model="overtime.rateType"
                            :disabled="disabled || isTOIL"
                        />

                    </v-flex>

                </v-layout>

                <!-- BREAK -->
                <v-layout row wrap>

                    <v-flex xs12 sm6 :class="isMobile ? '' : 'pr-10'">

                        <v-text-field
                            label="Break time (in minutes)"
                            v-model="overtime.breakTime"
                            :rules="[rules.required]"
                            type="text"
                            inputmode="numeric"
                            mask="###"
                        />

                    </v-flex>

                </v-layout>

                <!-- HOURS -->
                <v-layout row class="form-row-hours">

                    <v-flex xs6>
                        Total Hours: <b>{{ totalHours }}</b>
                    </v-flex>

                    <v-flex xs6>
                        Chargeable Hours: <b>{{ chargeableHours }}</b>
                    </v-flex>

                </v-layout>

                <!-- REMARKS -->
                <v-layout row v-if="showComment">

                    <v-flex xs12>

                        <v-textarea
                            v-model="overtime.remarks"
                            label="Remarks"
                            rows="2"
                            color="primary"
                        />

                    </v-flex>

                </v-layout>

            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment = !showComment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn v-if="overtime.mode != 'Edit'" @click="clearForm" :disabled="disabled">
                    Clear
                </v-btn>
                <v-btn color="primary" type="submit" :loading="loading" :disabled="disabled || !chargeableHours" class="mr-20">
                    {{ overtime.mode == 'Edit' ? 'Update' : 'Save' }}
                </v-btn>
            </v-card-actions>

        </v-card>
    </v-form>
</template>

<script>

import overtimeApi from '@/api/overtime'
import commonfn from '@/common/functions'
import overtimeRateTypes from '@/constants/overtime-rate-types'

export default {
    props: {
        showTransDatePicker: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            overtimeRateTypes,
            overtime: null,
            cutoff: null,
            transDate: null,
            menuTransDate: false,
            menuClockStart: false,
            menuClockEnd: false,
            timeClockStart: null,
            timeClockEnd: null,
            totalHours: null,
            chargeableHours: null,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: '',
            showComment: false,
            offsetOrPay_list: [
                {
                    offsetOrPay: 1,
                    Description: "Time Off in Lieu (TOIL)"
                },
                {
                    offsetOrPay: 2,
                    Description: "Payroll (Paid Overtime)"
                }
            ],
            rules: {
                required: value => !!value || 'Required.'
            }
        }
    },
    methods: {
        allowedDates(val) {
            if (!this.cutoff) return true

            return (
                this.$moment(this.cutoff.cutoffStart).format('YYYY-MM-DD')
                <= this.$moment(val).format('YYYY-MM-DD')
                &&
                this.$moment(this.cutoff.cutoffEnd).format('YYYY-MM-DD')
                >= this.$moment(val).format('YYYY-MM-DD')
            )
        },
        setRateType() {
            if (this.isTOIL)
                this.overtime.rateType = 4
            else
                this.overtime.rateType = this.notApplicableRateType ? null : 1
        },
        computeTotalHours() {
            if (
                !this.timeClockStart ||
                !this.timeClockEnd ||
                this.overtime.breakTime == null
            ) {
                return
            }

            let clockStart = this.$moment('1/1/1900 ' + this.timeClockStart)
            let clockEnd = this.$moment('1/1/1900 ' + this.timeClockEnd)

            if (clockStart >= clockEnd) {
                clockEnd = this.$moment('1/2/1900 ' + this.timeClockEnd)
            }

            const durationTotal =
                this.$moment.duration(clockEnd.diff(clockStart))

            const durationChargeable =
                this.$moment.duration(
                    clockEnd.clone()
                        .add(-this.overtime.breakTime, 'm')
                        .diff(clockStart)
                )

            this.totalHours = `${durationTotal.hours()}:${durationTotal.minutes()}`
            this.chargeableHours = `${durationChargeable.hours()}:${durationChargeable.minutes()}`
        },
        async setParams(data) {
            this.overtime = data
            this.cutoff = data.cutoff || null
            this.transDate = data.transDate || null

            if (data.mode == 'Edit') {
                this.timeClockStart = this.$moment(data.startTime).format('H:mm')
                this.timeClockEnd = this.$moment(data.endTime).format('H:mm')
                this.chargeableHours = data.chargeableHours

                this.computeTotalHours()
            }
        },
        async addEditOvertime() {

            this.computeTotalHours()
            await this.$nextTick()

            if (!this.$refs.form.validate())
                return

            try {
                this.loading = true
                this.disabled = true

                const selectedDate =
                    this.showTransDatePicker
                        ? this.transDate
                        : this.overtime.transDate

                const payload = {
                    overtimeId:
                        this.overtime.mode == 'Edit'
                            ? this.overtime.overtimeId
                            : 0,
                    transDate: selectedDate,
                    breakTime: this.overtime.breakTime,
                    chargeableHours: this.chargeableHours,
                    offsetOrPay: this.overtime.offsetOrPay,
                    rateType: this.overtime.rateType,                    
                    remarks: this.overtime.remarks,
                    startTime: this.$moment(selectedDate).format('MM/DD/YYYY') + ' ' + this.timeClockStart,
                    endTime: this.$moment(selectedDate).format('MM/DD/YYYY') + ' ' + this.timeClockEnd
                }

                let response = null

                if (this.overtime.mode == 'Edit')
                    response = await overtimeApi.update(payload)
                else
                    response = await overtimeApi.add(payload)

                if (response && response.status == 200) {

                    this.closeDialog()
                    this.$emit('send', '')
                }

            } catch (error) {
                this.errorMessage = error
                this.error = true

            } finally {
                this.loading = false
                this.disabled = false
            }
        },
        clearForm() {
            this.$refs.form.reset()

            this.totalHours = null
            this.chargeableHours = null

            this.loading = false
            this.error = false
        },
        closeDialog() {
            this.clearForm()
            this.$emit('update:dialogOvertime', false)
        }
    },
    watch: {
        timeClockStart() {
            this.computeTotalHours()
        },
        timeClockEnd() {
            this.computeTotalHours()
        },
        'overtime.breakTime'() {
            this.computeTotalHours()
        }
    },
    computed: {
        isTOIL() {
            return this.overtime && this.overtime.offsetOrPay == 1
        },
        isApproved() {
            return this.overtime && this.overtime.status == commonfn.getStatusCode('Approved')
        },
        overtimeRateTypesOptions() {
            if (this.isTOIL)
                return this.overtimeRateTypes

            return this.overtimeRateTypes.filter(x => x.paidOption)
        },
        notApplicableRateType() {
            return this.overtime && this.overtime.rateType == 4
        }
    }
}
</script>
<style scoped>
.form-content {
    padding:20px 35px 10px 35px;
}
.form-content-mobile {
    padding:20px 40px 10px 40px;
}
.form-row-hours {
    margin:10px 0px 10px 0px;
}
</style>